import React from 'react';
import { Box, ButtonBase, Checkbox, MenuItem, MenuList, Popover, Typography } from '@mui/material';
import ViewColumnRounded from '@mui/icons-material/ViewColumnRounded';
import { ToolbarButton } from '../inputs/ToolbarButton';

export interface ColumnPickerColumn {
  key: string;
  /** The column's name, in plain text. */
  name: string;
  /** Cannot be hidden — the one that says which row you are looking at. */
  fixed?: boolean;
}

export interface ColumnPickerProps {
  columns: ColumnPickerColumn[];
  /** Keys of the columns currently hidden. */
  hidden: string[];
  onChange: (hidden: string[]) => void;
  /** Names the table this belongs to, for screen readers. */
  label: string;
  /** Room for the word "Columns" beside the icon. Icon alone when false. */
  roomy?: boolean;
  /**
   * Leave out and the picker draws its own button. Passed, the list is opened
   * from somewhere else — an item in a panel's overflow menu — and hangs off
   * `anchorEl`.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorEl?: HTMLElement | null;
}

/**
 * Which columns of a wide table to show, for as long as you are looking at it.
 *
 * A table of fifteen columns is read a few columns at a time, and scrolling
 * sideways to compare two of them loses the ones in between. Turning the rest
 * off puts the two side by side.
 *
 * Nothing is saved. The choice is a way of looking at the figures rather than
 * a setting, so the table comes back whole on the next visit, and a download
 * still carries every column — what was taken away was only in the way.
 */
export function ColumnPicker({
  columns, hidden, onChange, label, roomy = true, open, onOpenChange, anchorEl,
}: ColumnPickerProps) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const controlled = open !== undefined;
  const isOpen = controlled ? Boolean(open) : Boolean(anchor);
  const close = () => (controlled ? onOpenChange?.(false) : setAnchor(null));
  const shown = columns.length - hidden.length;

  const toggle = (key: string) => {
    if (hidden.includes(key)) {
      onChange(hidden.filter((k) => k !== key));
      return;
    }
    // Never leave a table with nothing in it.
    if (shown <= 1) return;
    onChange([...hidden, key]);
  };

  return (
    <>
      {!controlled && (
      <Box component="span" ref={(el: HTMLElement | null) => setHost(el)} sx={{ display: 'inline-flex' }}>
        <ToolbarButton
          icon={<ViewColumnRounded fontSize="small" />}
          onClick={() => setAnchor(host)}
          aria-label={`Choose columns for ${label}`}
          tooltip={hidden.length ? `Showing ${shown} of ${columns.length} columns` : 'Choose columns'}
          selected={hidden.length > 0}
          count={hidden.length ? shown : undefined}
        >
          {roomy ? 'Columns' : undefined}
        </ToolbarButton>
      </Box>
      )}

      <Popover
        open={isOpen}
        anchorEl={controlled ? anchorEl : anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (t) => ({
              mt: 0.5,
              width: 260,
              maxHeight: 420,
              bgcolor: t.tokens.surface.overlay,
              border: `1px solid ${t.tokens.border.subtle}`,
              borderRadius: `${t.ds.radius.md}px`,
              boxShadow: t.tokens.shadow.md,
            }),
          },
        }}
      >
        <MenuList dense sx={{ py: 0.5 }}>
          {columns.map((c) => {
            const isShown = !hidden.includes(c.key);
            const locked = Boolean(c.fixed) || (isShown && shown <= 1);
            return (
              <MenuItem
                key={c.key}
                onClick={() => !locked && toggle(c.key)}
                disabled={locked}
                sx={{ py: 0.25 }}
              >
                <Checkbox
                  size="small"
                  checked={isShown || Boolean(c.fixed)}
                  disabled={locked}
                  tabIndex={-1}
                  sx={{ mr: 1, p: 0.5 }}
                />
                <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                  {c.name}
                </Typography>
              </MenuItem>
            );
          })}
        </MenuList>
        {hidden.length > 0 && (
          <Box sx={(t) => ({ borderTop: `1px solid ${t.tokens.border.subtle}`, px: 1, py: 0.5 })}>
            <ButtonBase
              onClick={() => onChange([])}
              sx={(t) => ({
                ...t.typography.caption,
                px: 1,
                py: 0.5,
                borderRadius: `${t.ds.radius.sm}px`,
                color: t.tokens.text.accent,
                '&:hover': { bgcolor: t.tokens.surface.sunken },
              })}
            >
              Show all {columns.length}
            </ButtonBase>
          </Box>
        )}
      </Popover>
    </>
  );
}
